import axios from 'axios';
import cache from './cache';
import { getLocalUserData } from './localUser';
import { NUMBERS_API_ENDPOINT, TRACK_API_ENDPOINT } from '../const/endpoint';

const CACHE_VALID_IN_MS = 3600000;
const cacheEnabled = false;
const apiClient = axios.create();

if (!cacheEnabled) {
  cache.remove();
}

apiClient.interceptors.request.use(
  (request) => {
    setRequestAuthentication(request);
    getCachedResultsForGetRequests(request);
    return request;
  },
  (error) => Promise.reject(error),
);

apiClient.interceptors.response.use(
  (response) => {
    setCachedResultForGetResponse(response);
    return response;
  },
  (error) => Promise.reject(error),
);

const setRequestAuthentication = (request) => {
  const loggedUser = getLocalUserData();
  if (loggedUser && loggedUser.token && isSavedNumbersRequest(request.url)) {
    request.headers.Authorization = `Bearer ${loggedUser.token}`;
  }
};

const resolveUrl = (url) => new URL(url, window.location.origin);

const isSavedNumbersRequest = (url) => {
  const requestUrl = resolveUrl(url);
  const numbersUrl = resolveUrl(NUMBERS_API_ENDPOINT);

  return (
    requestUrl.origin === numbersUrl.origin &&
    (requestUrl.pathname === numbersUrl.pathname ||
      requestUrl.pathname.startsWith(`${numbersUrl.pathname}/`))
  );
};

const isPublicTrackingRequest = ({ method, url }) => {
  if (method !== 'get') {
    return false;
  }

  const requestUrl = resolveUrl(url);
  const trackingUrl = resolveUrl(TRACK_API_ENDPOINT);

  return (
    requestUrl.origin === trackingUrl.origin &&
    requestUrl.pathname === trackingUrl.pathname
  );
};

const getCachedResultsForGetRequests = (request) => {
  const { url } = request;
  const { onLine } = navigator;

  if (cacheEnabled && isPublicTrackingRequest(request)) {
    const _cached = cache.get(url);

    if (_cached && (isCacheValid(_cached) || !onLine)) {
      request.data = _cached.data;
      request.__fromCache = true;
      request.adapter = () => {
        return Promise.resolve({
          data: _cached.data,
          status: 200,
          statusText: 'OK',
          headers: request.headers,
          config: request,
          request,
        });
      };
    }
  }
};

const isCacheValid = (cachedData) => {
  const { savedTime, ttl } = cachedData;
  return !ttl || new Date().getTime() - savedTime <= ttl;
};

const setCachedResultForGetResponse = (response) => {
  const { data, config } = response;
  const { url } = config;

  if (cacheEnabled && isPublicTrackingRequest(config) && !config.__fromCache) {
    cache.set(url, data, CACHE_VALID_IN_MS);
  }
};

export const clearRequestCache = () => cache.remove();

export default apiClient;
